import { base64Encode, base64Decode, gzipCompress, gzipDecompress } from '../../Utils/EncodingUtils';
import { resetQueryClient } from '../Query/QueryClient';
import { clearAutoLoginSuppression, removeAuthTokenFromUrl } from '../../Utils/AuthAutoLogin';

const sharedTextDecoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : undefined;
const MAX_PENDING_MESSAGES = 200;
const HUMAN_ROOM_PROTOCOL_CAPABILITIES = {
    protocolVersion: 2,
    clientType: 'human',
    stateProfile: 'human-ui',
    gameplayEvents: true
};
type LobbyType = 'normal' | 'tournament' | 'tournament_rooms' | 'tournament_rankings';
type LobbyJoinOptions = Record<string, unknown>;

const decodeText = (input: ArrayBuffer | Uint8Array) => {
    if (sharedTextDecoder) {
        return sharedTextDecoder.decode(input);
    }
    return new TextDecoder().decode(input);
};

export class SocketProvider {

    public _ws: WebSocket | null = null;
    public _on: Record<string, (type?: string, event?: string, data?: any) => void>;
    private _isConnected: boolean = false;
    private _pendingMessages: { type: string, event: string, data: any, roomID?: string }[] = [];
    private heartbeatInterval: any;
    private url: string;
    private reconnectAttempts: number = 0;
    private _shouldReconnect: boolean = true;
    private enableEncoding: boolean = false;
    private enableCompression: boolean = false;
    // Liveness and reconnect policy
    private lastMsgAt: number = Date.now();
    private pingIntervalMs: number = 10000;
    private staleThresholdMs: number = 30000;
    private baseReconnectMs: number = 2000;
    private maxReconnectMs: number = 30000;
    private hasEmittedReconnecting: boolean = false;
    private reconnectTimeoutId: any = null;
    private socketId: string = '';

    constructor(url: string, options?: { enableEncoding?: boolean, enableCompression?: boolean }) {
        this.url = url;
        this._on = {};
        if (options) {
            if (typeof options.enableEncoding === 'boolean') this.enableEncoding = options.enableEncoding;
            if (typeof options.enableCompression === 'boolean') this.enableCompression = options.enableCompression;
        }
        this.enableEncoding = import.meta.env.VITE_ENABLE_ENCODING === 'true';
        this.enableCompression = import.meta.env.VITE_ENABLE_COMPRESSION === 'true';
        this.pingIntervalMs = 10000;
        this.staleThresholdMs = 30000;
        this.baseReconnectMs = 2000;
        this.maxReconnectMs = 30000;

        // Network awareness
        window.addEventListener('online', () => {
            this.reconnectAttempts = 0;
            this._shouldReconnect = true;
            // If not connected, try now
            if (!this._isConnected) {
                this.setupWebSocket();
            }
        });
        window.addEventListener('offline', () => {
            this._shouldReconnect = false;
            this.closeConnection();
        });
    }

    public setupWebSocket() {
        if (this._ws) {
            if (this._ws.readyState === WebSocket.OPEN || this._ws.readyState === WebSocket.CONNECTING) {
                return;
            }
            this._ws.close();
            this.clearHeartbeat();
            this._ws = null;
        }
        this._ws = new WebSocket(this.url);
        this._ws.onopen = this.handleOpen;
        this._ws.onmessage = this.handleMessage;
        this._ws.onclose = this.handleDisconnected;
        this._ws.onerror = this.handleError;
    }

    private handleDisconnected = (): void => {
        this._isConnected = false;
        this.clearHeartbeat();
        this._on['disconnected']?.();

        // Only attempt reconnect if we should and we are online
        if (this._shouldReconnect && navigator.onLine) {
            const backoff = Math.min(this.baseReconnectMs * Math.pow(2, this.reconnectAttempts), this.maxReconnectMs);
            const jitter = Math.floor(Math.random() * 300);
            this.reconnectAttempts++;
            if (!this.hasEmittedReconnecting) {
                this.hasEmittedReconnecting = true;
                this._on['reconnecting']?.();
            }
            if (this.reconnectTimeoutId) {
                return;
            }
            this.reconnectTimeoutId = setTimeout(() => {
                this.reconnectTimeoutId = null;
                this.setupWebSocket();
            }, backoff + jitter);
        }
    }

    private handleError = (event: Event): void => {
        console.error(`WebSocket error:`, event);

        // Properly close the connection on error
        if (this._ws && this._ws.readyState !== WebSocket.CLOSED) {
            try {
                this._isConnected = false;
                this.clearHeartbeat();
                this._ws.close();
            } catch (err) {
                console.error('Error closing WebSocket connection:', err);
            }
        }

        // Notify error handlers
        this._on['error']?.(undefined, 'connection_error', event);

        // Attempt reconnection if appropriate
        if (this._shouldReconnect && navigator.onLine) {
            const backoff = Math.min(this.baseReconnectMs * Math.pow(2, this.reconnectAttempts), this.maxReconnectMs);
            const jitter = Math.floor(Math.random() * 300);
            this.reconnectAttempts++;
            if (!this.hasEmittedReconnecting) {
                this.hasEmittedReconnecting = true;
                this._on['reconnecting']?.();
            }
            if (this.reconnectTimeoutId) {
                return;
            }
            this.reconnectTimeoutId = setTimeout(() => {
                this.reconnectTimeoutId = null;
                this.setupWebSocket();
            }, backoff + jitter);
        }

    }

    public clearHeartbeat() {
        clearInterval(this.heartbeatInterval);
    }

    public logIn = (username: string, password: string, hsToken?: string): void => {
        this._shouldReconnect = true;

        // If not connected, connect first then send credentials
        if (!this._isConnected || !this._ws || this._ws.readyState !== WebSocket.OPEN) {
            const sessionToken = localStorage.getItem('sessionToken');

            // Store login request for after connection
            this._pendingMessages = [{
                type: 'authentication',
                event: hsToken ? 'loginWithHsToken' : sessionToken && !username ? 'loginWithToken' : 'login',
                data: hsToken ? { token: hsToken } :
                    sessionToken && !username ? { token: sessionToken } :
                        { username, password }
            }];

            this.setupWebSocket();
        } else {
            // Already connected, send credentials directly
            const sessionToken = localStorage.getItem('sessionToken');

            if (hsToken) {
                this.send('authentication', 'loginWithHsToken', { token: hsToken });
            } else if (sessionToken && !username) {
                this.send('authentication', 'loginWithToken', { token: sessionToken });
            } else if (username && password) {
                this.send('authentication', 'login', { username, password });
            }
        }
    }

    public logOut = (userId: string): void => {
        this.send('authentication', 'logout', { userId });
        this.clearHeartbeat();
        this._shouldReconnect = false;
        // Clear session token on logout
        localStorage.removeItem('sessionToken');
        void resetQueryClient();
        this.closeConnection();
    }

    public confirmLoginTakeover = (conflictId: string): void => {
        this.send('authentication', 'confirmLoginTakeover', { conflictId });
    }

    public cancelLoginTakeover = (conflictId: string): void => {
        this.send('authentication', 'cancelLoginTakeover', { conflictId });
    }

    public get on(): Record<string, (type?: string, event?: string, data?: any) => void> {
        return this._on;
    }

    public startHeartbeat() {
        this.heartbeatInterval = setInterval(() => {
            if (this._ws?.readyState === WebSocket.OPEN) {
                this._ws.send('ping');
                // Stale link detection: if no messages for threshold, force close to trigger reconnect
                if (Date.now() - this.lastMsgAt > this.staleThresholdMs) {
                    try {
                        this._ws.close();
                    } catch {}
                }
            }
        }, this.pingIntervalMs);
    }

    private handlePong = () => {
        this.lastMsgAt = Date.now();
    }

    private handleOpen = (): void => {
        this._isConnected = true;
        this.hasEmittedReconnecting = false;
        // Assign a new client-side socket id on each successful open
        this.socketId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = null;
        }
        this.reconnectAttempts = 0;
        this.startHeartbeat();

        // Notify connection established
        this._on['connected']?.();

        // Process any pending messages
        if (this._pendingMessages.length > 0) {
            this._pendingMessages.forEach(({ type, event, data, roomID }) => {
                this.send(type, event, data, roomID);
            });
            this._pendingMessages = [];
        }
    }

    public handleMessage = (event: MessageEvent): void => {
        if (event.data === 'pong') {
            this.handlePong();
            return;
        }
        if (!event.data) {
            console.warn('Received empty message from server');
            return;
        }
        let decoded: string | undefined;
        try {
            let dataToDecode: any = event.data;
            if (this.enableEncoding) {
                dataToDecode = base64Decode(dataToDecode);
            }
            if (this.enableCompression) {
                decoded = gzipDecompress(dataToDecode, true) as string;
            } else if (this.enableEncoding) {
                decoded = decodeText(dataToDecode);
            } else {
                decoded = dataToDecode;
            }
            if (typeof decoded !== 'string') {
                console.warn('Decoded message is not a string:', decoded);
                return;
            }
            const { type, event: eventName, data } = JSON.parse(decoded) as { type: string, event: string, data: any };
            // Update last message time for liveness
            this.lastMsgAt = Date.now();

            // Handle login failure / notAuthenticated
            if (type === 'authentication' && (eventName === 'loginFailed' || eventName === 'notAuthenticated')) {
                // Clear invalid token so subsequent reconnect cycles can prompt or retry HS token
                localStorage.removeItem('sessionToken');
                void resetQueryClient();
            }

            // Store session token if login was successful
            if (type === 'authentication' && eventName === 'loginSuccess') {
                if (data.sessionToken) {
                    localStorage.setItem('sessionToken', data.sessionToken);
                }
                clearAutoLoginSuppression();
                void resetQueryClient();
                this.reconnectAttempts = 0;
                this.send('app', 'clientCapabilities', { ...HUMAN_ROOM_PROTOCOL_CAPABILITIES });

                // Clean up URL if it was an HS token login
                removeAuthTokenFromUrl();
            }

            // Use a try-catch block for the message handler to prevent uncaught exceptions
            try {
                if (this._on['message']) {
                    this._on['message'](type, eventName, data);
                }
            } catch (handlerError) {
                console.error(`Error in message handler for ${type}/${eventName}:`, handlerError);
            }
        } catch (parseError) {
            console.error(`Failed to parse message:`, parseError, event.data);
        }
    }

    public send(type: string, event: string, data: any, roomID?: string): void {
        let message = JSON.stringify({ type, event, data, roomID });
        let out: string | Uint8Array = message;
        if (this.enableCompression) {
            out = gzipCompress(out);
        }
        if (this.enableEncoding) {
            out = base64Encode(out);
        }
        if (this._isConnected && this._ws?.readyState === WebSocket.OPEN) {
            this._ws.send(typeof out === 'string' ? out : decodeText(out));
        } else {
            if (this._pendingMessages.length >= MAX_PENDING_MESSAGES) {
                this._pendingMessages.shift();
            }
            this._pendingMessages.push({ type, event, data, roomID });
        }
    }

    public getSocketId(): string {
        return this.socketId;
    }

    public closeConnection(): void {
        this.clearHeartbeat();
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = null;
        }
        this._ws?.close();
        this._ws = null;
    }

    public joinRoom = (roomId: string): void => {
        this.send('room', 'join', { roomId, ...HUMAN_ROOM_PROTOCOL_CAPABILITIES });
    }

    public leaveRoom = (roomId: string): void => {
        this.send('room', 'leave', { roomId });
    }

    public joinLobby = (lobbyType: LobbyType = 'tournament', options: LobbyJoinOptions = {}): void => {
        this.send('lobby', 'join', { ...options, type: lobbyType });
    }

    public leaveLobby = (lobbyType?: LobbyType): void => {
        this.send('lobby', 'leave', { type: lobbyType });
    }

    public sitOnRoom = (roomId: string, seatIndex: number, buyInAmount: number): void => {
        this.send('room', 'sit', { roomId, seatIndex, buyInAmount });
    }

    public standFromRoom = (roomId: string): void => {
        this.send('room', 'stand', { roomId });
    }

    public requestRoomState = (roomId: string): void => {
        this.send('room', 'requestState', { roomId });
    }

    public requestSit = (roomId: string, seatIndex: number, buyInAmount: number, postOrWaitChoice?: 'post' | 'wait'): void => {
        this.send('room', 'requestSit', {
            roomId,
            seatIndex,
            buyInAmount,
            postOrWaitChoice
        });
    }

    public requestSitOptions = (roomId: string, seatIndex: number): void => {
        this.send('room', 'requestSitOptions', {
            roomId,
            seatIndex
        });
    }

    public sendBalanceUpdate = (roomId: string, userId: string, balance: number): void => {
        this.send('room', 'updateBalance', {
            roomId,
            userId,
            balance
        });
    }

    public disconnect = (): void => {
        this._shouldReconnect = false;
        this.clearHeartbeat();
        this.closeConnection();
    }

    public setEncoding(enabled: boolean) {
        this.enableEncoding = enabled;
    }

    public setCompression(enabled: boolean) {
        this.enableCompression = enabled;
    }

}
